// src/redux/identity/townlitVerificationSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import townlitVerificationApi from "../../services/identity/townlitVerificationApi";
import type {
  TownlitVerificationEligibilityDTO,
  TownlitVerificationState,
  TownlitVerificationStatusDTO,
} from "../../types/identity/townlitVerificationTypes";

// Fetch TownLIT gold eligibility
export const fetchTownlitVerificationEligibility = createAsyncThunk<
  TownlitVerificationEligibilityDTO,
  void,
  { rejectValue: string }
>("townlitVerification/fetchEligibility", async (_, { rejectWithValue }) => {
  try {
    return await townlitVerificationApi.fetchEligibility();
  } catch (error: any) {
    const rawDetail = error?.response?.data?.detail;
    const detail = Array.isArray(rawDetail) ? rawDetail.join(" ") : rawDetail;

    return rejectWithValue(
      detail || "Failed to load TownLIT verification eligibility."
    );
  }
});

// Fetch TownLIT gold status
export const fetchTownlitVerificationStatus = createAsyncThunk<
  TownlitVerificationStatusDTO,
  void,
  { rejectValue: string }
>("townlitVerification/fetchStatus", async (_, { rejectWithValue }) => {
  try {
    return await townlitVerificationApi.fetchStatus();
  } catch (error: any) {
    const rawDetail = error?.response?.data?.detail;
    const detail = Array.isArray(rawDetail) ? rawDetail.join(" ") : rawDetail;

    return rejectWithValue(
      detail || "Failed to load TownLIT verification status."
    );
  }
});

const initialState: TownlitVerificationState = {
  eligibility: null,
  statusData: null,

  eligibilityStatus: "idle",
  statusStatus: "idle",

  error: null,
};

const townlitVerificationSlice = createSlice({
  name: "townlitVerification",
  initialState,
  reducers: {
    clearTownlitVerificationError(state) {
      state.error = null;
    },
    resetTownlitVerificationState(state) {
      state.eligibility = null;
      state.statusData = null;
      state.eligibilityStatus = "idle";
      state.statusStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Eligibility
      .addCase(fetchTownlitVerificationEligibility.pending, (state) => {
        state.eligibilityStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTownlitVerificationEligibility.fulfilled, (state, action) => {
        state.eligibilityStatus = "succeeded";
        state.eligibility = action.payload;
      })
      .addCase(fetchTownlitVerificationEligibility.rejected, (state, action) => {
        state.eligibilityStatus = "failed";
        state.error =
          action.payload || "Failed to load TownLIT verification eligibility.";
      })

      // Status
      .addCase(fetchTownlitVerificationStatus.pending, (state) => {
        state.statusStatus = "loading";
        state.error = null;
      })
      .addCase(fetchTownlitVerificationStatus.fulfilled, (state, action) => {
        state.statusStatus = "succeeded";
        state.statusData = action.payload;
      })
      .addCase(fetchTownlitVerificationStatus.rejected, (state, action) => {
        state.statusStatus = "failed";
        state.error =
          action.payload || "Failed to load TownLIT verification status.";
      });
  },
});

export const {
  clearTownlitVerificationError,
  resetTownlitVerificationState,
} = townlitVerificationSlice.actions;

export default townlitVerificationSlice.reducer;